import { BrowserUtils } from "ts_utils";
import { SongDO } from "./data/SongDO";
import { PlayerInitializer } from "./player/PlayerInitializer";
import { RAControls, RAControlsEvent, RAControlsEventType }
	from "./player/RAControls";
import { FbInit } from "./services/firebase/FbInit";
import { AppSettings } from "./settings/AppSettings";
import { MainMenu, MainMenuEvent, MainMenuEventType }
	from "./songSelection/MainMenu";
import { AppContainer, AppScreen } from "./ui/AppContainer";
import * as analytics from "./utilities/analytics";

export class RecorderAce {
	private _mainMenu:MainMenu;
	private _raControls:RAControls;
	
	private _state:AppState;
	
	constructor(){
		this._init();
	}
	
	private async _init():Promise<void> {
		//init Firestore to enable persistent storage
		await FbInit.initializeFirebase(true);
		
		//clear query params and set start page history to song menu
		this._setStartPageHistory();
		
		//init app container
		AppContainer.get();
		
		//create the MainMenu
		this._state = AppState.initializingMainMenu;
		this._mainMenu = new MainMenu();
		this._mainMenu.event.on(this._onMainMenuEvent);
		
		//create PlayerWindow
		this._state = AppState.initializingPlayer;
		let initializer = new PlayerInitializer();
		await initializer.loadApplication();
		this._raControls = initializer.raControls;
		
		this._raControls.event.on(this._onRaControlsEvent);

		//MainMenu loading completes complete asynchronously, so the remaining 
		//lines in this method will execute before the MainMenu completes
		AppContainer.get().setActiveScreen(AppScreen.menu); 
		await this._showSongMenu(false);
		
		//listen for back/forward buttons
		window.addEventListener("popstate", this._onPopState);
	}
	
	private _onMainMenuEvent = (e:MainMenuEvent):void => {
		if (e.type === MainMenuEventType.loadSongRequest){
			//update the url
			let query:string = this._buildSongQuery(e.songDO);
			window.history.pushState({'page':'play'}, "",
				`${AppSettings.pageUrl}${query}`);
			
			//update page title to the new song
			document.title = e.songDO.title?? "Custom Song";
			
			analytics.updateAnalyticsPage(query, document.title);
			
			//call onPopState
			this._onPopState(null);
		}
	}
	
	private _buildSongQuery(sdo:SongDO):string {
		return BrowserUtils.buildQueryString(new Map([
			["id", sdo.libId]
		]));
	}
	
	private _onPopState = async (e:PopStateEvent):Promise<void> => {
		if (window.history.state["page"] === "menu"){
			this._raControls.closeSong();
			AppContainer.get().setActiveScreen(AppScreen.menu);
			await this._showSongMenu();
		}
		else {
			this._mainMenu.deactivate();
			this._state = AppState.playerSongLoading;
			AppContainer.get().setActiveScreen(AppScreen.player);
			await this._raControls.openSong(this._mainMenu.currentSongDO);
			this._state = AppState.playerActive;
		}
	}
	
	private _onRaControlsEvent = (e:RAControlsEvent):void => {
		switch (e.type){
			case RAControlsEventType.mainMenuRequest:
				window.history.back();
				break;
		}
	}
	
	private async _showSongMenu(updateAnalytics:boolean = true):Promise<void> {
		this._state = AppState.songMenuLoading;
		document.title = "RecorderAce";
		if (updateAnalytics){
			analytics.updateAnalyticsPage("", "RecorderAce");
		}

		//this completes asynchronously, but it shouldn't be a problem.
		await this._mainMenu.activate();
		this._state = AppState.songMenuActive;
	}
	
	private _setStartPageHistory():void {
		//update the query string to remove song ids and pop indexes.
		let query:string = BrowserUtils.getUpdatedQueryString(['id']);
		
		//we'll always start history on the menu page, regardless
		document.title = "RecorderAce";
		window.history.replaceState({"page":"menu"}, "", 
			`${AppSettings.pageUrl}${query}`);
	}
}

enum AppState {
	startScreen,
	initializingMainMenu,
	initializingPlayer,
	songMenuLoading,
	songMenuActive,
	playerSongLoading,
	playerActive
}